// The Dashboard's Tag / Exclude Tag pickers (components/TagMultiSelect.js).
// The server filters by these lists case-insensitively (backend/utils/tagFilter.js),
// so "VIP" and "vip" are one tag here too.

// Mirror backend/utils/tagFilter.js MAX_FILTER_TAGS (the server drops the rest)
export const MAX_FILTER_TAGS = 50;

const tagKey = (tag) => tag.trim().toLowerCase();
const isTagText = (tag) => typeof tag === 'string' && tag.trim() !== '';

/**
 * Whether a tag is in the selection, compared case-insensitively.
 * @param {string[]|null|undefined} selected
 * @param {string} tag
 * @returns {boolean}
 */
export function isTagSelected(selected, tag) {
  if (!Array.isArray(selected) || !isTagText(tag)) return false;
  const key = tagKey(tag);
  return selected.some(s => isTagText(s) && tagKey(s) === key);
}

/**
 * Ticks or unticks a tag: a picked tag (any spelling) leaves, an unpicked one
 * joins at the end (not past MAX_FILTER_TAGS). A blank tag changes nothing.
 * @param {string[]|null|undefined} selected never changed
 * @param {string} tag
 * @returns {string[]} a new array
 */
export function toggleTag(selected, tag) {
  const list = Array.isArray(selected) ? selected : [];
  if (!isTagText(tag)) return [...list];
  if (isTagSelected(list, tag)) {
    const key = tagKey(tag);
    return list.filter(s => !(isTagText(s) && tagKey(s) === key));
  }
  if (list.length >= MAX_FILTER_TAGS) return [...list];
  return [...list, tag];
}

/**
 * The picker's checkbox list: the inventory's tags in their order (spellings
 * of one tag once, the first kept), then any selected tag no longer in the
 * inventory (so it can be unticked), narrowed by the search text
 * (case-insensitive substring; blank = all).
 * @param {string[]|null|undefined} options useInventoryTags data
 * @param {string[]|null|undefined} selected
 * @param {string|null|undefined} query the search box
 * @returns {{ tag: string, isSelected: boolean }[]}
 */
export function tagChoices(options, selected, query) {
  const picked = Array.isArray(selected) ? selected : [];
  const all = [...(Array.isArray(options) ? options : []), ...picked];
  const q = String(query ?? '').trim().toLowerCase();
  const seen = new Set();
  const out = [];
  for (const tag of all) {
    if (!isTagText(tag)) continue;
    const key = tagKey(tag);
    if (seen.has(key)) continue;
    seen.add(key);
    if (q && !key.includes(q)) continue;
    out.push({ tag, isSelected: isTagSelected(picked, tag) });
  }
  return out;
}
